import React, { useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { Button, Modal, Space, Tooltip } from 'antd'
import { MinusOutlined, VideoCameraOutlined } from '@ant-design/icons'
import ScreenRecordingStudio from './ScreenRecordingStudio'
import { useScreenRecordingContext } from './RecordingContext'
import { formatTime } from './utils'
import Helpers from '../../util/Helpers'
import './screenRecording.css'

// Menu action gating this icon — a user needs this action, on the Documentation menu,
// to see the global screen recorder at all. Looked up by menu NAME (see
// Helpers.canDoActionByName) rather than a hardcoded (appId, menuId) pair like
// EmployeeCenter's EMPLOYEE_CENTER_MENU_ID — the Documentation menu isn't scoped to one
// specific app the way Employee Center is, so matching by its menu_name ("Documentation",
// as registered in Administration > Menus) avoids hardcoding a menu_id that could be wrong.
const DOCUMENTATION_MENU_NAME = 'Documentation'
const DOCUMENTATION_ACTIONS = {
  createDocumentation: 'create_documentation',
}

/**
 * Floating "Record your screen" button + its modal, mounted once in the app
 * shell (MainApp) so it's available on every authenticated page — not just
 * the Documentation "Screen Recording" wizard.
 *
 * Minimizable on purpose: recording (getDisplayMedia/MediaRecorder) doesn't
 * care whether its dialog is visible, only whether <ScreenRecordingStudio/>
 * is still mounted. So "minimize" here just hides the big dialog
 * (`modalVisible = false`) while leaving the Studio instance mounted
 * (`sessionActive` stays true) — the recording keeps running in the
 * background, and a small status pill takes the dialog's place so the user
 * can get back to it. Any dismissal gesture on the dialog itself (the X,
 * Esc, the dedicated minimize button) only minimizes — the recording is
 * only ever actually stopped/discarded via the Studio's own explicit
 * "Cancel" button, which is what unmounts it for real.
 */
const GlobalScreenRecorder = () => {
  const history = useHistory()
  const { setPendingRecording } = useScreenRecordingContext()
  const { actionAuthorizations, applications } = useSelector(({ auth }) => auth)
   console.log('Action buttones')
   console.table(applications, actionAuthorizations, DOCUMENTATION_MENU_NAME,
    DOCUMENTATION_ACTIONS.createDocumentation)
  const canCreateDocumentation = Helpers.canDoActionByName(
    applications,
    actionAuthorizations,
    DOCUMENTATION_MENU_NAME,
    DOCUMENTATION_ACTIONS.createDocumentation,
  )

  const [sessionActive, setSessionActive] = useState(false) // a Studio instance is live (mounted)
  const [modalVisible, setModalVisible] = useState(false) // whether the big dialog is showing right now
  const [liveStatus, setLiveStatus] = useState(null) // { status, elapsedSec, clipCount, hasFinal }

  const handleOpen = () => {
    setSessionActive(true)
    setModalVisible(true)
  }

  const handleMinimize = () => setModalVisible(false)
  const handleResume = () => setModalVisible(true)

  const resetSession = () => {
    setSessionActive(false)
    setModalVisible(false)
    setLiveStatus(null)
  }

  const handleDiscard = () => resetSession()

  const handleComplete = (file, meta) => {
    setPendingRecording(file, meta)
    resetSession()
    history.push('/company/documentation/create/screen_recording')
  }

  const isLive = liveStatus?.status === 'recording' || liveStatus?.status === 'paused'
  const pillLabel = liveStatus?.hasFinal
    ? 'Recording ready — click to finish'
    : isLive
      ? `${liveStatus.status === 'paused' ? 'Paused' : 'Recording'} · ${formatTime(liveStatus.elapsedSec || 0)}`
      : liveStatus?.clipCount
        ? `${liveStatus.clipCount} clip${liveStatus.clipCount > 1 ? 's' : ''} recorded`
        : 'Screen recorder open'

  // Hide the icon entirely for users without the create_documentation menu action — a
  // recording only ever leads into creating a Documentation entry (handleComplete above
  // routes straight to the Screen Recording wizard), so there's nothing for someone without
  // that permission to do with it.
  if (!canCreateDocumentation) {
    return null
  }

  return (
    <>
      {!sessionActive && (
        <Tooltip title='Record your screen' placement='left'>
          <button
            type='button'
            className='sr-global-fab'
            onClick={handleOpen}
            aria-label='Record your screen'
          >
            <VideoCameraOutlined />
          </button>
        </Tooltip>
      )}

      {sessionActive && !modalVisible && (
        <Tooltip title='Resume — recording continues in the background' placement='left'>
          <button
            type='button'
            className='sr-global-pill'
            onClick={handleResume}
            aria-label='Resume screen recording'
          >
            {isLive && <span className='sr-rec-dot'>●</span>}
            <span>{pillLabel}</span>
          </button>
        </Tooltip>
      )}

      {sessionActive && (
        <Modal
          visible={modalVisible}
          onCancel={handleMinimize}
          footer={null}
          maskClosable={false}
          width={860}
          title={
            <Space>
              <span>Record your screen</span>
              <Tooltip title='Minimize — keep recording in the background'>
                <Button size='small' type='text' icon={<MinusOutlined />} onClick={handleMinimize} />
              </Tooltip>
            </Space>
          }
          className='sr-global-modal'
        >
          <ScreenRecordingStudio
            onComplete={handleComplete}
            onCancel={handleDiscard}
            onStatusChange={setLiveStatus}
          />
        </Modal>
      )}
    </>
  )
}

export default GlobalScreenRecorder
